import { useState, useRef } from 'react';

const FILE_TYPES = [
  { key: 'financials', label: 'Financials', icon: '📊', accept: '.pdf,.xlsx,.xls,.csv,.doc,.docx', desc: 'P&L, budget, financial snapshot' },
  { key: 'menu', label: 'Menu', icon: '🍽️', accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx', desc: 'Menu PDF or photos' },
  { key: 'deck', label: 'Deck / Docs', icon: '📄', accept: '.pdf,.pptx,.ppt,.doc,.docx', desc: 'Pitch deck, concept docs' },
  { key: 'other', label: 'Other', icon: '📎', accept: '*', desc: 'Any supporting files' },
];

export default function FileUploadPanel({ audit, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const fileRefs = useRef({});

  const files = audit.intake?.files || {};
  const textFields = {
    financialSnapshot: audit.intake?.financialSnapshot || '',
    menuNotes: audit.intake?.menuNotes || '',
    deckNotes: audit.intake?.deckNotes || '',
  };

  const fileCount = Object.values(files).filter(Boolean).length;
  const textCount = Object.values(textFields).filter(v => v.trim()).length;
  const totalAttachments = fileCount + textCount;

  const handleFileChange = (key, e) => {
    const file = e.target.files[0];
    if (file) {
      const updatedFiles = { ...files, [key]: file };
      onUpdate({ ...audit, intake: { ...audit.intake, files: updatedFiles } });
    }
  };

  const handleTextChange = (key, value) => {
    onUpdate({ ...audit, intake: { ...audit.intake, [key]: value } });
  };

  const removeFile = (key) => {
    const updatedFiles = { ...files };
    delete updatedFiles[key];
    onUpdate({ ...audit, intake: { ...audit.intake, files: updatedFiles } });
    if (fileRefs.current[key]) fileRefs.current[key].value = '';
  };

  return (
    <div className="card mb-4 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">📎</span>
          <span className="text-xs text-neutral-400">Files & Supporting Data</span>
          {totalAttachments > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold font-medium">{totalAttachments}</span>
          )}
        </div>
        <span className="text-neutral-600 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3 space-y-3">
          {FILE_TYPES.map(ft => (
            <div key={ft.key} className="flex items-start gap-3">
              <span className="text-lg mt-0.5">{ft.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-300 font-medium">{ft.label}</p>
                <p className="text-[10px] text-neutral-600">{ft.desc}</p>

                {files[ft.key] ? (
                  <div className="flex items-center gap-2 mt-1.5 bg-neutral-800/40 rounded px-2 py-1.5">
                    <span className="text-xs text-neutral-300 truncate flex-1">{files[ft.key].name}</span>
                    <span className="text-[10px] text-neutral-600">{(files[ft.key].size / 1024).toFixed(0)}KB</span>
                    <button onClick={() => removeFile(ft.key)} className="text-[10px] text-neutral-500 hover:text-red-400">✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRefs.current[ft.key]?.click()}
                    className="mt-1.5 text-[11px] px-2.5 py-1 rounded bg-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-700 transition-all"
                  >
                    Upload
                  </button>
                )}
                <input
                  ref={el => fileRefs.current[ft.key] = el}
                  type="file"
                  accept={ft.accept}
                  onChange={(e) => handleFileChange(ft.key, e)}
                  className="hidden"
                />
              </div>
            </div>
          ))}

          {/* Text fields for pasted data */}
          <div className="border-t border-neutral-800 pt-3 mt-3">
            <p className="text-[10px] text-neutral-600 mb-2">Or paste key data directly:</p>
            <textarea
              value={textFields.financialSnapshot}
              onChange={(e) => handleTextChange('financialSnapshot', e.target.value)}
              placeholder="Paste financial data..."
              rows={2}
              className="input-field text-xs mb-2"
            />
            <textarea
              value={textFields.menuNotes}
              onChange={(e) => handleTextChange('menuNotes', e.target.value)}
              placeholder="Paste menu details..."
              rows={2}
              className="input-field text-xs mb-2"
            />
            <textarea
              value={textFields.deckNotes}
              onChange={(e) => handleTextChange('deckNotes', e.target.value)}
              placeholder="Paste deck/concept notes..."
              rows={2}
              className="input-field text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
}
